import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  advanceChapterIntro,
  advanceSceneIntro,
  applyDraftChoice,
  createInitialRunState,
  endScenePlay,
  fillNarratorText,
  getChapterForSceneIndex,
  getCurrentScene,
  loadMemories,
  loadMeta,
  resumeSceneIndex,
  loadScarIds,
  loadTutorialProgress,
  saveTutorialProgress,
  memoryLineForOutcome,
  playCard,
  pushMemory,
  saveMeta,
  unlockScar,
  type RunScope,
  type SoloRunState,
} from '@plotjack/core';
import { BOOK1_STORY } from '../data/story';
import { webStorageAdapter } from '../adapters/localStorage';
import { TUTORIAL_INFO_STEPS, TUTORIAL_STEPS } from '../lib/tutorialSteps';
import type { TutorialStep } from '../components/TutorialOverlay';

export type Screen = 'title' | 'game' | 'recap';

export function useGame() {
  const [screen, setScreen] = useState<Screen>('title');
  const [chosenEnding, setChosenEnding] = useState('ending_hope');
  const [runScope, setRunScope] = useState<RunScope>('full');
  const [chapterId, setChapterId] = useState<string>('ch1_protocol');
  const [state, setState] = useState<SoloRunState | null>(null);
  const [memories, setMemories] = useState<string[]>([]);
  const [scars, setScars] = useState<string[]>([]);
  const [chaptersCompleted, setChaptersCompleted] = useState<string[]>([]);
  const [recapLines, setRecapLines] = useState<string[]>([]);
  const [tutorialActive, setTutorialActive] = useState(false);
  const [tutorialStepIndex, setTutorialStepIndex] = useState(0);
  const [pendingTutorialReplay, setPendingTutorialReplay] = useState(false);
  const [showPrologue, setShowPrologue] = useState(false);

  useEffect(() => {
    void (async () => {
      setMemories(await loadMemories(webStorageAdapter));
      setScars(await loadScarIds(webStorageAdapter));
      const meta = await loadMeta(webStorageAdapter);
      setChaptersCompleted(meta.chaptersCompleted ?? []);
    })();
  }, []);

  const tutorialStep: TutorialStep | null = tutorialActive
    ? TUTORIAL_STEPS[tutorialStepIndex] ?? null
    : null;

  const tutorialShowNext = tutorialStep !== null && TUTORIAL_INFO_STEPS.includes(tutorialStep.id);

  const enableTutorialForRun = useCallback(async (opts?: { force?: boolean }) => {
    const progress = await loadTutorialProgress(webStorageAdapter);
    if (opts?.force) {
      setTutorialActive(true);
      setTutorialStepIndex(0);
      return;
    }
    if (!progress.completed && !progress.skipped) {
      setTutorialActive(true);
      setTutorialStepIndex(0);
    } else {
      setTutorialActive(false);
    }
  }, []);

  const skipTutorial = useCallback(async () => {
    setTutorialActive(false);
    await saveTutorialProgress(webStorageAdapter, {
      version: 1,
      completed: false,
      skipped: true,
      lastStepId: TUTORIAL_STEPS[tutorialStepIndex]?.id,
    });
  }, [tutorialStepIndex]);

  const completeTutorial = useCallback(async () => {
    setTutorialActive(false);
    await saveTutorialProgress(webStorageAdapter, {
      version: 1,
      completed: true,
      skipped: false,
      lastStepId: 'draft',
    });
  }, []);

  const advanceTutorialStep = useCallback(() => {
    setTutorialStepIndex((i) => Math.min(i + 1, TUTORIAL_STEPS.length - 1));
  }, []);

  const onTutorialEvent = useCallback(
    (event: 'card_selected' | 'target_selected' | 'card_played' | 'draft_phase') => {
      if (!tutorialActive) return;
      const id = TUTORIAL_STEPS[tutorialStepIndex]?.id;
      if (event === 'card_selected' && id === 'select_card') {
        setTutorialStepIndex((i) => i + 1);
      }
      if (event === 'target_selected' && id === 'select_target') {
        setTutorialStepIndex((i) => i + 1);
      }
      if (event === 'card_played' && (id === 'play' || id === 'select_target' || id === 'select_card')) {
        const playIdx = TUTORIAL_STEPS.findIndex((s) => s.id === 'outcome');
        if (playIdx >= 0) setTutorialStepIndex(playIdx);
      }
      if (event === 'draft_phase') {
        const draftIdx = TUTORIAL_STEPS.findIndex((s) => s.id === 'draft');
        if (draftIdx >= 0) setTutorialStepIndex(draftIdx);
      }
    },
    [tutorialActive, tutorialStepIndex, completeTutorial],
  );

  const onTutorialNext = useCallback(() => {
    const id = TUTORIAL_STEPS[tutorialStepIndex]?.id;
    if (id === 'draft') {
      void completeTutorial();
      return;
    }
    advanceTutorialStep();
  }, [tutorialStepIndex, advanceTutorialStep, completeTutorial]);

  const scene = useMemo(() => {
    if (!state) return null;
    return getCurrentScene(BOOK1_STORY, state);
  }, [state]);

  const activeChapter = useMemo(() => {
    if (!state) return null;
    return getChapterForSceneIndex(BOOK1_STORY, state.sceneIndex);
  }, [state]);

  const narratorBody = useMemo(() => {
    if (!state || !scene) return '';
    return fillNarratorText(scene.narratorText, state.variables);
  }, [state, scene]);

  const startRun = useCallback(
    async (opts?: { continueSaved?: boolean }) => {
      const scarIds = await loadScarIds(webStorageAdapter);
      const mem = await loadMemories(webStorageAdapter);
      const meta = await loadMeta(webStorageAdapter);
      setMemories(mem);
      setScars(scarIds);
      setChaptersCompleted(meta.chaptersCompleted ?? []);

      let carry;
      let sceneIndex;
      if (opts?.continueSaved && meta.continueRun) {
        const saved = meta.continueRun;
        carry = saved;
        sceneIndex = resumeSceneIndex(saved);
        setChosenEnding(saved.chosenEndingId);
        setRunScope(saved.runScope);
        setChapterId(saved.chapterId ?? 'ch1_protocol');
      }

      const run = createInitialRunState(
        opts?.continueSaved && meta.continueRun ? meta.continueRun.chosenEndingId : chosenEnding,
        { story: BOOK1_STORY, scarCardIds: scarIds },
        {
          scope: opts?.continueSaved && meta.continueRun ? meta.continueRun.runScope : runScope,
          chapterId:
            opts?.continueSaved && meta.continueRun
              ? meta.continueRun.chapterId ?? undefined
              : runScope === 'chapter'
                ? chapterId
                : undefined,
          sceneIndex,
          carry,
        },
      );
      setState(run);
      setRecapLines([]);
      setScreen('game');
      // New runs that start at the very beginning open with the illustrated cold open
      setShowPrologue(!opts?.continueSaved && run.sceneIndex === 0 && !!BOOK1_STORY.prologue?.length);

      const forceTutorial = pendingTutorialReplay;
      setPendingTutorialReplay(false);
      if (forceTutorial) {
        await enableTutorialForRun({ force: true });
      } else if (!opts?.continueSaved && run.sceneIndex === 0) {
        await enableTutorialForRun();
      } else {
        setTutorialActive(false);
      }
    },
    [chosenEnding, runScope, chapterId, pendingTutorialReplay, enableTutorialForRun],
  );

  const startTutorialRun = useCallback(() => {
    setRunScope('chapter');
    setChapterId('ch1_protocol');
    setPendingTutorialReplay(true);
    void startRun();
  }, [startRun]);

  const beginChapter = useCallback(() => {
    setState((s) => (s ? advanceChapterIntro(s) : s));
  }, []);

  const beginScene = useCallback(() => {
    setState((s) => (s ? advanceSceneIntro(s) : s));
    if (tutorialActive && TUTORIAL_STEPS[tutorialStepIndex]?.id === 'ink') {
      // Stay on ink until player taps Got it
    }
  }, [tutorialActive, tutorialStepIndex]);

  const onPlayCard = useCallback(
    (cardId: string, beatId: string) => {
      setState((s) => {
        if (!s) return s;
        const next = playCard(s, cardId, beatId, {
          story: BOOK1_STORY,
          scarCardIds: scars,
        });
        const change = next.history?.[next.history.length - 1];
        if (next !== s && scene && change) {
          void pushMemory(webStorageAdapter, `Last time, in "${scene.title}", you ${change.did}.`);
        } else if (next !== s && next.lastOutcome && scene) {
          void pushMemory(webStorageAdapter, memoryLineForOutcome(next.lastOutcome, scene.title));
        }
        return next;
      });
    },
    [scars, scene],
  );

  const finishScene = useCallback(() => {
    setState((s) => {
      if (!s) return s;
      const next = endScenePlay(s, { story: BOOK1_STORY, scarCardIds: scars });
      if (next.phase === 'run_end') {
        void finalizeRun(next);
        setScreen('recap');
      } else if (next.phase === 'draft') {
        void saveContinue(next);
      }
      return next;
    });
  }, [scars]);

  const saveContinue = async (s: SoloRunState) => {
    const meta = await loadMeta(webStorageAdapter);
    await saveMeta(webStorageAdapter, {
      ...meta,
      continueRun: {
        chosenEndingId: s.chosenEndingId,
        runScope: s.runScope,
        chapterId: s.chapterId,
        sceneIndex: s.sceneIndex,
        // saved during the draft after finishing sceneIndex, or at the start of the next scene
        resumeIndex: s.phase === 'draft' ? s.sceneIndex + 1 : s.sceneIndex,
        dread: s.dread,
        tracks: s.tracks,
        deck: s.deck,
        hand: s.hand,
        discard: s.discard,
        drawPile: s.drawPile,
        variables: s.variables,
        keepsakes: s.keepsakes,
        callbacks: s.callbacks,
        history: s.history,
        sceneLog: s.sceneLog,
        metCast: s.metCast,
        savedAt: new Date().toISOString(),
      },
    });
  };

  const finalizeRun = async (finalState: SoloRunState) => {
    const ch = getChapterForSceneIndex(BOOK1_STORY, finalState.sceneIndex);
    const lines = (finalState.sceneLog ?? []).map((e) => `${e.title}: ${e.summary}`);
    setRecapLines(lines);
    const ending = BOOK1_STORY.endings.find((e) => e.id === finalState.endingReached);
    if (ending) {
      const updated = await unlockScar(webStorageAdapter, ending.scarCardId);
      setScars(updated);
    }
    const meta = await loadMeta(webStorageAdapter);
    const completed = ch ? [...new Set([...(meta.chaptersCompleted ?? []), ch.id])] : meta.chaptersCompleted;
    await saveMeta(webStorageAdapter, {
      ...meta,
      totalRuns: meta.totalRuns + 1,
      endingsSeen: ending
        ? [...new Set([...meta.endingsSeen, ending.id])]
        : meta.endingsSeen,
      chaptersCompleted: completed,
      continueRun: null,
    });
    if (completed) setChaptersCompleted(completed);
  };

  const draft = useCallback(
    (choice: 'take' | 'upgrade' | 'burn' | 'skip', id?: string) => {
      setState((s) => {
        if (!s) return s;
        const next = applyDraftChoice(s, choice, id, { story: BOOK1_STORY, scarCardIds: scars });
        // keep the save in step with the deck change and the scene we are about to open
        if (next.phase === 'scene_intro' || next.phase === 'chapter_intro') void saveContinue(next);
        return next;
      });
    },
    [scars],
  );

  return {
    screen,
    setScreen,
    chosenEnding,
    setChosenEnding,
    runScope,
    setRunScope,
    chapterId,
    setChapterId,
    state,
    scene,
    activeChapter,
    narratorBody,
    memories,
    scars,
    chaptersCompleted,
    recapLines,
    startRun,
    beginChapter,
    beginScene,
    onPlayCard,
    finishScene,
    draft,
    story: BOOK1_STORY,
    tutorialStep,
    tutorialShowNext,
    onTutorialNext,
    skipTutorial,
    onTutorialEvent,
    startTutorialRun,
    showPrologue,
    finishPrologue: () => setShowPrologue(false),
  };
}
